import { useState, useCallback, useRef } from "react";
// WAF CAPTCHA response detection
const WAF_CAPTCHA_STATUS = 405;
const WAF_CAPTCHA_HEADER = 'x-amzn-waf-action';
/**
 * Check if a response is a WAF CAPTCHA challenge
 */ export function isWafCaptchaResponse(response) {
    // WAF returns 405 for CAPTCHA challenges
    if (response.status === WAF_CAPTCHA_STATUS) {
        return true;
    }
    // Also check for WAF header
    const wafAction = response.headers.get(WAF_CAPTCHA_HEADER);
    if (wafAction === 'captcha') {
        return true;
    }
    return false;
}
/**
 * Fetch wrapper that handles WAF CAPTCHA challenges
 * Uses the AWS WAF Integration fetch if available
 */ export async function fetchWithWafCaptcha(url, options) {
    // If AWS WAF Integration is loaded, use its fetch
    if (window.AwsWafIntegration?.fetch) {
        return window.AwsWafIntegration.fetch(url, options);
    }
    // Fallback to regular fetch
    return fetch(url, options);
}
/**
 * Hook for managing WAF CAPTCHA state and rendering
 */ export function useWafCaptcha(options = {}) {
    const [state, setState] = useState({
        isRequired: false,
        isLoading: false,
        error: null
    });
    const captchaContainerRef = useRef(null);
    const pendingRequestRef = useRef(null);
    /**
   * Render CAPTCHA in a container
   */ const renderCaptcha = useCallback((container)=>{
        captchaContainerRef.current = container;
        const apiKey = import.meta.env.VITE_WAF_CAPTCHA_API_KEY;
        if (!apiKey) {
            console.error('WAF CAPTCHA API key not configured');
            setState((prev)=>({
                    ...prev,
                    error: 'CAPTCHA not configured'
                }));
            return;
        }
        if (!window.AwsWafCaptcha) {
            console.error('AWS WAF CAPTCHA SDK not loaded');
            setState((prev)=>({
                    ...prev,
                    error: 'CAPTCHA SDK not loaded'
                }));
            return;
        }
        setState((prev)=>({
                ...prev,
                isLoading: true
            }));
        window.AwsWafCaptcha.renderCaptcha(container, {
            apiKey,
            onSuccess: async (token)=>{
                setState((prev)=>({
                        ...prev,
                        isRequired: false,
                        isLoading: false
                    }));
                options.onCaptchaSolved?.();
                // Retry the pending request with the CAPTCHA token
                if (pendingRequestRef.current) {
                    const { url, options: fetchOptions, resolve, reject } = pendingRequestRef.current;
                    pendingRequestRef.current = null;
                    try {
                        const response = await fetch(url, {
                            ...fetchOptions,
                            headers: {
                                ...fetchOptions?.headers,
                                'x-aws-waf-token': token
                            }
                        });
                        resolve(response);
                    } catch (error) {
                        reject(error);
                    }
                }
            },
            onError: (error)=>{
                setState((prev)=>({
                        ...prev,
                        isLoading: false,
                        error: error.message
                    }));
                options.onCaptchaError?.(error.message);
            },
            onLoad: ()=>{
                setState((prev)=>({
                        ...prev,
                        isLoading: false
                    }));
            }
        });
    }, [
        options
    ]);
    /**
   * Handle a response that might be a CAPTCHA challenge
   */ const handleResponse = useCallback(async (response, url, fetchOptions)=>{
        if (isWafCaptchaResponse(response)) {
            setState((prev)=>({
                    ...prev,
                    isRequired: true
                }));
            options.onCaptchaRequired?.();
            // Return a promise that resolves when CAPTCHA is solved
            return new Promise((resolve, reject)=>{
                pendingRequestRef.current = {
                    url,
                    options: fetchOptions,
                    resolve,
                    reject
                };
            });
        }
        return response;
    }, [
        options
    ]);
    /**
   * Reset CAPTCHA state and reject any pending requests
   */ const reset = useCallback(()=>{
        // Reject any pending request before clearing
        if (pendingRequestRef.current) {
            pendingRequestRef.current.reject(new Error('CAPTCHA cancelled by user'));
            pendingRequestRef.current = null;
        }
        setState({
            isRequired: false,
            isLoading: false,
            error: null
        });
    }, []);
    /**
   * Cancel CAPTCHA without rejecting (for when user will retry)
   */ const cancel = useCallback(()=>{
        if (pendingRequestRef.current) {
            pendingRequestRef.current.reject(new Error('CAPTCHA_CANCELLED'));
            pendingRequestRef.current = null;
        }
        setState({
            isRequired: false,
            isLoading: false,
            error: null
        });
    }, []);
    return {
        ...state,
        renderCaptcha,
        handleResponse,
        reset,
        cancel
    };
}
export default useWafCaptcha;
